import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import { easeOutQuint } from 'app/utils/customFunctions'
import { motion } from 'framer-motion'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  description?: string
  image?: ImageProps
  label?: string
  layout?: number
  logo?: string
}

export const Intro = memo(function Intro({
  description,
  image,
  label,
  layout = 1,
  logo,
}: Props) {
  if (!description) {
    return null
  }

  const [ref, isVisible] = useInView({
    triggerOnce: true,
  })

  const variants = {
    visible: {
      clipPath: 'inset(0)',
      transition: {
        delay: 0.4,
        duration: 2,
        ease: easeOutQuint,
      },
    },
    hidden: {
      clipPath: 'inset(0 0 0 100%)',
    },
  }

  return (
    <Container className={layout !== 1 ? 'dark' : undefined}>
      <Head>
        {logo ? (
          <FadeInUp>
            <Logo alt="Mare Hotel" src={logo} width={130} height={72} />
          </FadeInUp>
        ) : null}

        {label ? <Label tag="h2" text={label} /> : null}

        {description ? <Description text={description} /> : null}
      </Head>

      {image ? (
        <Background
          animate={isVisible ? 'visible' : 'hidden'}
          initial="hidden"
          ref={ref}
          variants={variants}
        >
          <Image {...image} />
        </Background>
      ) : null}
    </Container>
  )
})

const Head = styled.div`
  max-width: 63rem;
  margin: auto;
  text-align: center;

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Logo = styled(Image)`
  display: block;
  width: auto;
  height: 4.5rem;
  margin: 0 auto 3rem;

  img {
    display: block;
    height: 100%;
    margin: auto;
  }

  @media (max-width: 1199px) {
    height: 3rem;
    margin-bottom: 2rem;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}
`

const Description = styled(AnimatedTitle)`
  ${styles.claim}
`

const Background = styled(motion.div)`
  height: 88.889vh;
  max-height: 50rem;
  margin-top: 6rem;
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    aspect-ratio: 1.85;
    height: auto;
    max-height: none;
    margin-top: 3.75rem;
  }
`

const Container = styled.section`
  padding: 9rem 5vw 0;
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 75%;
    background: url('/background-light.webp') no-repeat top center;
    background-size: cover;
    position: absolute;
    top: 0;
    left: 0;
  }
  &.dark {
    &:before {
      background-image: url('/background-dark-01.webp');
    }

    ${Description} {
      color: ${({ theme }) => theme.colors.variants.neutralLight1};
    }
  }

  @media (max-width: 1199px) {
    padding: 3.75rem 2rem 0;
    &:before {
      height: 85%;
    }
  }
`
